import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { CurrencyPipe } from '@angular/common';
import { LucideAngularModule, LucideIconProvider, LUCIDE_ICONS, icons } from 'lucide-angular';
import {
  AdminInvoiceListItem,
  INVOICE_TYPE_LABELS,
  InvoiceType,
} from '../../../models/invoice.model';
import { AdminInvoiceService } from '../../../services/admin-invoice.service';

@Component({
  selector: 'app-admin-invoices',
  standalone: true,
  imports: [FormsModule, RouterLink, CurrencyPipe, LucideAngularModule],
  providers: [{ provide: LUCIDE_ICONS, multi: true, useValue: new LucideIconProvider(icons) }],
  templateUrl: './admin-invoices.component.html',
})
export class AdminInvoicesComponent implements OnInit {
  private readonly invoices = inject(AdminInvoiceService);

  readonly loading = signal(true);
  readonly loadError = signal<string | null>(null);
  readonly rows = signal<AdminInvoiceListItem[]>([]);
  readonly printingId = signal<string | null>(null);
  readonly generating = signal(false);
  readonly generateError = signal<string | null>(null);
  readonly generateSuccess = signal<string | null>(null);
  readonly backfilling = signal(false);
  readonly backfillMsg = signal<string | null>(null);
  readonly repairingMissing = signal(false);
  readonly repairMissingMsg = signal<string | null>(null);

  readonly typeLabels = INVOICE_TYPE_LABELS;

  bookingIdInput = '';

  async ngOnInit(): Promise<void> {
    await this.reload();
  }

  async reload(): Promise<void> {
    this.loading.set(true);
    this.loadError.set(null);
    try {
      const list = await this.invoices.listInvoices(100);
      this.rows.set(list);
    } catch (err) {
      const msg = this.formatCallableError(err);
      this.loadError.set(msg);
    } finally {
      this.loading.set(false);
    }
  }

  formatEuros(cents: number): number {
    return cents / 100;
  }

  async print(invoice: AdminInvoiceListItem): Promise<void> {
    this.printingId.set(invoice.id);
    try {
      const { html, invoiceNumber } = await this.invoices.getInvoiceHtml(invoice.id);
      this.invoices.openPrintWindow(html, invoiceNumber);
    } catch (err) {
      this.loadError.set(err instanceof Error ? err.message : 'No se pudo abrir la factura.');
    } finally {
      this.printingId.set(null);
    }
  }

  async generate(): Promise<void> {
    const bookingId = this.bookingIdInput.trim();
    if (!bookingId) {
      this.generateError.set('Indica el ID de la reserva.');
      return;
    }

    const types: InvoiceType[] = ['PLATFORM_COMMISSION'];

    this.generating.set(true);
    this.generateError.set(null);
    this.generateSuccess.set(null);
    try {
      const result = await this.invoices.generateBookingInvoices(bookingId, types);
      const created = result.results.filter((r) => r.invoiceNumber && !r.skipped);
      const skipped = result.results.filter((r) => r.skipped);
      if (created.length === 0 && skipped.length > 0) {
        this.generateError.set(
          'No se generó ninguna factura (comisión 0, sin pago o datos fiscales incompletos).',
        );
      } else if (created.length > 0) {
        this.generateSuccess.set(
          created.map((r) => `${r.type}: ${r.invoiceNumber}`).join(' · '),
        );
        this.bookingIdInput = '';
        await this.reload();
      } else {
        this.generateSuccess.set('Las facturas ya existían o no aplicaban.');
        await this.reload();
      }
    } catch (err) {
      this.generateError.set(this.formatCallableError(err));
    } finally {
      this.generating.set(false);
    }
  }

  async backfillOwnerUids(): Promise<void> {
    if (this.backfilling()) return;
    this.backfilling.set(true);
    this.backfillMsg.set(null);
    try {
      const { scanned, updated } = await this.invoices.backfillOwnerUids();
      this.backfillMsg.set(`Revisadas ${scanned} · actualizadas ${updated}.`);
    } catch (err) {
      this.backfillMsg.set(this.formatCallableError(err));
    } finally {
      this.backfilling.set(false);
    }
  }

  async repairMissingInvoices(): Promise<void> {
    if (this.repairingMissing()) return;
    this.repairingMissing.set(true);
    this.repairMissingMsg.set(null);
    try {
      const result = await this.invoices.repairMissingInvoices(500);
      const summary =
        `Revisadas ${result.scanned} · pendientes ${result.pending} · ` +
        `creadas ${result.created} · reenlazadas ${result.relinked}`;
      if (result.failed > 0 || result.skipped > 0) {
        const firstFailure = result.failures[0];
        const detail = firstFailure ?
          ` Primera incidencia: ${firstFailure.bookingId} — ${firstFailure.reason}` :
          '';
        this.repairMissingMsg.set(
          `${summary} · omitidas ${result.skipped} · errores ${result.failed}.${detail}`,
        );
      } else {
        this.repairMissingMsg.set(summary);
      }
      await this.reload();
    } catch (err) {
      this.repairMissingMsg.set(this.formatCallableError(err));
    } finally {
      this.repairingMissing.set(false);
    }
  }

  private formatCallableError(err: unknown): string {
    const code = typeof err === 'object' && err !== null && 'code' in err ?
      String((err as { code?: string }).code) :
      '';
    if (code === 'functions/not-found' || code === 'functions/unavailable') {
      return 'El servicio de facturas no está desplegado. Ejecuta deploy de Cloud Functions (listInvoicesAdmin).';
    }
    if (code === 'functions/permission-denied') {
      return 'Solo administradores pueden ver las facturas.';
    }
    if (code === 'functions/unauthenticated') {
      return 'Inicia sesión como administrador.';
    }
    return err instanceof Error ? err.message : 'No se pudieron cargar las facturas.';
  }
}
